import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CostCheckState,
  DictionaryValidationIndexEntry,
  DictionaryValidationListOptions,
} from "@shared-core/data/cost-engine-validation-types";
import {
  AVAILABLE_ROUTE_COUNT_SORT,
  type FailedMappingLeaf,
  type FulfilmentRoutePopulateStatus,
  type InHouseCatchUpRun,
  type RouteCoverage,
  type SupplierRouteUpkeep,
} from "@shared-core/data/fulfilment-route-types";
import type { DictionaryFamily } from "@shared-core/data/api-types";
import { productTypeLabel } from "@shared-core/domain/cost-engine-validation-presentation";
import {
  SUPPLIER_UPKEEP_STATES,
  formatAgo,
  formatFailedMappingLeaf,
  formatRouteCount,
  isBelowRouteTarget,
} from "@shared-core/domain/fulfilment-route-presentation";
import {
  formatCount,
  formatDateTimeOrDash,
  formatTimeOrDash,
} from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { OURS_THARSTERN_LEGEND } from "@shared-core/domain/engine-tharstern-comparison";
import { paths } from "@shared-core/routing/routes";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../app/components/primitives/tooltip";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { CostCheckCell } from "../app/components/domain/catalogue/CostCheckBadges";
import { ArrowUpDown, BookMarked, Search } from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

const PAGE_SIZE = 25;

// ── The dictionary list ───────────────────────────────────────────────────
// The plain per-item list (decision 12 Sep, `03_manufacturing_surfaces_
// design.md` "setup visibility and costing validation are separate"): Name,
// Product type, Live costing, Cost check, Used by. The validation-detail
// columns (Penny-exact / within 5%, Last checked, Rolling checks), and the
// feeds banner, live only on the Live costing › Checks tab's own "By item"
// view — this index lost both, 12 Sep, when that tab moved under Settings ›
// Catalogue › Live costing.
//
// Fulfilment routes (catalogue third-party fulfilment brief §8, journeys B
// and C): each item's count of available routes — in-house included (#24) —
// flagged below the three-route goal and sortable server-side, and the
// overnight banner above the list saying whether the supplier quotes and
// the in-house catch-up ran.

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "name", label: "Name (A–Z)" },
  {
    value: AVAILABLE_ROUTE_COUNT_SORT,
    label: "Fewest fulfilment routes first",
  },
  {
    value: `-${AVAILABLE_ROUTE_COUNT_SORT}`,
    label: "Most fulfilment routes first",
  },
];

// ── Families ──────────────────────────────────────────────────────────────
// The dictionary is navigated family › product: the family list (each family
// holding an item, in the dictionary's family order, with its count) beside
// the table, and the table filtered server-side to the chosen family
// (`?family=`). On a phone the list folds into a dropdown above the table.

type FamiliesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; families: DictionaryFamily[] };

function useDictionaryFamilies(): [FamiliesState, () => void] {
  const adapter = useDataAdapter();
  const [state, setState] = useState<FamiliesState>({ status: "loading" });
  const load = useCallback(() => {
    setState({ status: "loading" });
    adapter
      .listDictionaryFamilies()
      .then((families) => setState({ status: "loaded", families }))
      .catch(() => setState({ status: "error" }));
  }, [adapter]);
  useEffect(() => {
    load();
  }, [load]);
  return [state, load];
}

function FamilyNav({
  state,
  selected,
  onSelect,
  onRetry,
}: {
  state: FamiliesState;
  selected: string;
  onSelect: (family: string) => void;
  onRetry: () => void;
}) {
  if (state.status === "loading") {
    return <p className="text-sm text-muted-foreground">Loading families…</p>;
  }
  if (state.status === "error") {
    return (
      <div className="space-y-2 text-sm text-destructive">
        <p>Couldn&rsquo;t load the families.</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  const total = state.families.reduce((sum, f) => sum + f.item_count, 0);
  const entries = [
    { code: "", label: "All families", item_count: total },
    ...state.families,
  ];
  return (
    <>
      <select
        aria-label="Family"
        className="md:hidden w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        value={selected}
        onChange={(e) => onSelect(e.target.value)}
      >
        {entries.map((family) => (
          <option key={family.code} value={family.code}>
            {family.label} ({family.item_count})
          </option>
        ))}
      </select>
      <nav aria-label="Families" className="hidden md:block">
        <ul className="space-y-0.5">
          {entries.map((family) => {
            const isSelected = family.code === selected;
            return (
              <li key={family.code}>
                <button
                  type="button"
                  aria-current={isSelected ? "page" : undefined}
                  onClick={() => onSelect(family.code)}
                  className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ${
                    isSelected
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  }`}
                >
                  <span>{family.label}</span>
                  <span className="tabular-nums text-xs">
                    {family.item_count}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}

function DictionaryListTable() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [familiesState, reloadFamilies] = useDictionaryFamilies();

  const family = searchParams.get("family") ?? "";
  const sort = searchParams.get("sort") ?? "name";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const costCheckFilter = (searchParams.get("costCheck") ?? "") as
    CostCheckState | "";

  const [items, setItems] = useState<DictionaryValidationIndexEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = (p: number) =>
    updateParams({ page: p === 1 ? null : String(p) });

  const load = useCallback(() => {
    const opts: DictionaryValidationListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort,
    };
    if (debouncedQ) opts.q = debouncedQ;
    if (family) opts.family = family;
    if (costCheckFilter) opts.costCheckState = costCheckFilter;
    setLoadError(false);
    adapter
      .listDictionaryValidationItems(opts)
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, page, sort, debouncedQ, family, costCheckFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const families =
    familiesState.status === "loaded" ? familiesState.families : [];
  const familyLabel = (code: string | null | undefined) =>
    families.find((f) => f.code === code)?.label ?? "—";
  const isAllFamilies = family === "";
  const columnCount = isAllFamilies ? 6 : 5;

  return (
    <div className="grid gap-6 md:grid-cols-[13rem_minmax(0,1fr)]">
      <FamilyNav
        state={familiesState}
        selected={family}
        onSelect={(code) => updateParams({ family: code || null, page: null })}
        onRetry={reloadFamilies}
      />
      <div className="space-y-4 min-w-0">
        <h2 className="text-lg font-semibold text-foreground">
          {isAllFamilies ? "All families" : familyLabel(family)}
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[12rem]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                updateParams({ page: null });
              }}
              className="pl-9"
            />
          </div>
          <select
            aria-label="Filter by cost check"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={costCheckFilter}
            onChange={(e) =>
              updateParams({ costCheck: e.target.value || null, page: null })
            }
          >
            <option value="">All cost checks</option>
            <option value="green">Matches</option>
            <option value="amber">Small differences</option>
            <option value="red">Mismatch</option>
            <option value="grey">Not yet checked</option>
          </select>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="size-4 text-muted-foreground" />
            <select
              aria-label="Sort"
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={sort}
              onChange={(e) =>
                updateParams({
                  sort: e.target.value === "name" ? null : e.target.value,
                  page: null,
                })
              }
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                {isAllFamilies && <TableHead>Family</TableHead>}
                <TableHead>Product type</TableHead>
                <TableHead>Cost check</TableHead>
                <TableHead>Fulfilment routes</TableHead>
                <TableHead>Used by (catalogue items)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {firstLoad ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="text-center py-8 text-muted-foreground"
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              ) : loadError ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-destructive">
                      <p>Couldn&rsquo;t load the dictionary.</p>
                      <Button variant="outline" size="sm" onClick={load}>
                        Try again
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No dictionary items match these filters
                  </TableCell>
                </TableRow>
              ) : (
                items.map((entry) => (
                  <TableRow
                    key={entry.dictionaryItemId}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(
                        paths.dictionaryItemAdmin({
                          dictionaryItemId: entry.dictionaryItemId,
                        }),
                      )
                    }
                  >
                    <TableCell className="font-medium">{entry.name}</TableCell>
                    {isAllFamilies && (
                      <TableCell className="text-muted-foreground">
                        {familyLabel(entry.family)}
                      </TableCell>
                    )}
                    {entry.isMadeInHouse ? (
                      <>
                        <TableCell className="text-muted-foreground">
                          {productTypeLabel(
                            entry.productTypeId,
                            entry.productTypeName,
                          )}
                        </TableCell>
                        <TableCell>
                          <CostCheckCell
                            state={entry.costCheckState}
                            mismatchingCostLine={entry.mismatchingCostLine}
                            lastCheck={entry.lastCheck}
                          />
                        </TableCell>
                      </>
                    ) : (
                      // Supplier-only (decision #71): no Tharstern product
                      // type, so no cost checks.
                      <>
                        <TableCell className="text-sm text-muted-foreground">
                          Not made in-house
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          —
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <RouteCoverageCell coverage={entry.routeCoverage} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {entry.usedByCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />

        {!firstLoad && !loadError && (
          <p className="text-xs text-muted-foreground">
            {total} {total === 1 ? "item" : "items"}
          </p>
        )}
      </div>
    </div>
  );
}

/** "2 routes" with a quiet "Below 3" flag under the three-route goal (#20);
 *  the tooltip names each route and why an unavailable one does not count. */
function RouteCoverageCell({ coverage }: { coverage: RouteCoverage }) {
  const isBelowTarget = isBelowRouteTarget(coverage);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex items-center gap-1.5"
          aria-label={`${formatRouteCount(coverage.availableRouteCount)} available${isBelowTarget ? `, below the goal of ${coverage.target}` : ""}`}
        >
          <span className="tabular-nums">
            {formatRouteCount(coverage.availableRouteCount)}
          </span>
          {isBelowTarget && (
            <Badge variant="warning">Below {coverage.target}</Badge>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="space-y-0.5">
        {coverage.routes.map((r) => (
          <p key={r.route.code}>
            {r.route.label}: {r.isAvailable ? "available" : r.reason}
          </p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

// ── Overnight cost updates (journey C) ──────────────────────────────────
// "Is the Solopress column trustworthy today?" at a glance. One chained
// nightly run (#48), described in its own order: each supplier's product
// list, then its quotes, then the in-house catch-up over stale costs against
// its cap. The product-list snapshot id lives on the cost drill-down only.

function PopulateStatusBanner() {
  const adapter = useDataAdapter();
  const [status, setStatus] = useState<FulfilmentRoutePopulateStatus | null>(
    null,
  );
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .getFulfilmentRoutePopulateStatus()
      .then(setStatus)
      .catch(() => setLoadError(true));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  const now = currentTime();
  return (
    <section
      aria-label="Overnight cost updates"
      className="space-y-2 rounded-lg border border-border px-4 py-3 text-sm"
    >
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Overnight cost updates
      </h2>
      {loadError ? (
        <p className="text-destructive">
          Couldn&rsquo;t load the overnight cost updates.{" "}
          <button
            type="button"
            onClick={load}
            className="font-medium underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      ) : status === null ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <ul className="space-y-2">
          <li className="text-muted-foreground">
            {status.lastRun === null
              ? "The nightly run has not run yet."
              : `Last run ${formatDateTimeOrDash(status.lastRun.startedAt)}–${formatTimeOrDash(status.lastRun.completedAt)} (finished ${formatAgo(status.lastRun.completedAt, now)}), in one chain: ${[
                  ...status.supplierRoutes
                    .filter((u) => u.isConfigured)
                    .flatMap((u) => [
                      `${u.route.label} product list`,
                      `${u.route.label} quotes`,
                    ]),
                  "in-house stale costs",
                ].join(", then ")}.`}
          </li>
          {status.supplierRoutes.map((upkeep) => (
            <SupplierUpkeepLines
              key={upkeep.route.code}
              upkeep={upkeep}
              now={now}
            />
          ))}
          <InHouseCatchUpLine run={status.inHouseCatchUp} now={now} />
          <FailedMappingsLine leaves={status.failedLeaves} />
        </ul>
      )}
    </section>
  );
}

function SupplierUpkeepLines({
  upkeep,
  now,
}: {
  upkeep: SupplierRouteUpkeep;
  now: Date;
}) {
  const { label, tone } = SUPPLIER_UPKEEP_STATES[upkeep.state];
  const fetch = upkeep.lastQuoteFetch;
  const refresh = upkeep.lastProductListRefresh;
  return (
    <>
      {upkeep.isConfigured && (
        <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-medium">{upkeep.route.label} product list</span>
          <span className="text-muted-foreground">
            {refresh === null
              ? "Never refreshed."
              : refresh.isSucceeded
                ? `Refreshed ${formatDateTimeOrDash(refresh.completedAt)} (${formatAgo(refresh.completedAt, now)})`
                : `Refresh failed ${formatDateTimeOrDash(refresh.completedAt)}: ${refresh.failureReason}. Mappings are still checked against the previous product list.`}
          </span>
        </li>
      )}
      <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-medium">{upkeep.route.label} quotes</span>
        <Badge variant={tone}>{label}</Badge>
        <span className="text-muted-foreground">
          {!upkeep.isConfigured
            ? upkeep.notConfiguredReason
            : fetch === null
              ? "Never fetched."
              : `Last fetched ${formatDateTimeOrDash(fetch.startedAt)} (${formatAgo(fetch.startedAt, now)}): ${formatCount(fetch.succeeded)} of ${formatCount(fetch.attempted)} quotes fetched, ${formatCount(fetch.failed)} failed · ${fetch.callBudget == null ? `${formatCount(fetch.callsUsed)} calls used` : `${formatCount(fetch.callsUsed)} of ${formatCount(fetch.callBudget)} calls used`}`}
          {upkeep.state === "not_run_recently" &&
            ` · ${upkeep.route.label} quotes are expiring after one missed night and expire after 36 hours, when they drop out until the run happens again.`}
        </span>
        {fetch?.exampleFailureReason && (
          <span className="basis-full text-muted-foreground">
            For example: {fetch.exampleFailureReason}
          </span>
        )}
      </li>
    </>
  );
}

function InHouseCatchUpLine({
  run,
  now,
}: {
  run: InHouseCatchUpRun | null;
  now: Date;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-medium">In-house stale costs</span>
      <span className="text-muted-foreground">
        {run === null
          ? "Not yet run."
          : `Last night (${formatAgo(run.completedAt, now)}) re-costed ${formatCount(run.recosted)}${run.cap == null ? "" : ` of a nightly limit of ${formatCount(run.cap)}`}, ${formatCount(run.failed)} failed · ${formatCount(run.staleRemaining)} still stale`}
      </span>
    </li>
  );
}

/** Every mapping leaf that failed last night, item by item — the signal
 *  that a PR is needed. Each links to its item's mappings. */
function FailedMappingsLine({ leaves }: { leaves: FailedMappingLeaf[] }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-medium">Mappings</span>
      {leaves.length === 0 ? (
        <span className="text-muted-foreground">None failed last night.</span>
      ) : (
        <>
          <Badge variant="error">
            {formatCount(leaves.length)} failed —{" "}
            {leaves.length === 1 ? "needs" : "need"} a PR
          </Badge>
          <ul className="basis-full space-y-0.5 text-muted-foreground">
            {leaves.map((leaf) => (
              <li
                key={`${leaf.dictionaryItemId}:${leaf.backEnd.code}:${leaf.path.join("/")}`}
              >
                <Link
                  to={paths.dictionaryItemMappings({
                    dictionaryItemId: leaf.dictionaryItemId,
                  })}
                  className="underline-offset-2 hover:underline"
                >
                  {formatFailedMappingLeaf(leaf)}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </li>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

export function DictionaryAdminPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <BookMarked className="h-6 w-6" /> Dictionary
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every dictionary item, family by family, with its cost check state and
          how many fulfilment routes could make it today.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {OURS_THARSTERN_LEGEND}
        </p>
        <p className="text-sm mt-2">
          <Link
            to={paths.dictionaryGapLog()}
            className="font-medium underline underline-offset-2"
          >
            Dictionary gap log
          </Link>{" "}
          <span className="text-muted-foreground">
            &mdash; work the dictionary couldn&rsquo;t describe, as staff
            recorded it.
          </span>
        </p>
      </div>

      <PopulateStatusBanner />

      <DictionaryListTable />
    </div>
  );
}
